import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReduxStates } from '../../redux/reduxTypes';
import { isEqual } from 'lodash';

import { defaultMarketFilters } from '../../redux/reducers/filters';
import * as SavedActions from '../../redux/actions/saved';

import PulseLoader from 'react-spinners/PulseLoader';

import FilterOption from './components/FilterOption';

import { ModalFilter, FilterOption as FO } from '../../types';
import { returnFormattedSegmentFilters } from '../../helpers/format';
import { fetchCall } from '../../services/api';
import { colors } from '../../base/variables.styled';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';
import { ReactComponent as ExitIcon } from '../../assets/icons/exit-icon.svg';
import BaseButton from '../BaseButton';

interface Props {
  values: Array<ModalFilter | null>;
  onUpdate: (filters: Array<ModalFilter | null>) => void;
}

const FilterModal = ({ onUpdate, values }: Props): React.ReactElement => {
  const savedMarket = useSelector((state: ReduxStates) => state.saved.savedMarket);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isGlobalLoading, setIsGlobalLoading] = useState<boolean>(false);
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const config = useSelector((state: ReduxStates) => state.config);

  const dispatch = useDispatch();

  const [regionFilters, setRegionFilters] = useState<ModalFilter | null>(values[0] || null);
  const [marketFilters, setMarketFilters] = useState<ModalFilter | null>(values[1] || null);
  const [channelFilters, setChannelFilters] = useState<ModalFilter | null>(values[2] || null);
  const [retailerFilters, setRetailerFilters] = useState<ModalFilter | null>(values[3] || null);
  const [categoryFilters, setCategoryFilters] = useState<ModalFilter | null>(values[4] || null);
  const [subcategoryFilters, setSubCategoryFilters] = useState<ModalFilter | null>(values[5] || null);
  const [segmentFilters, setSegmentFilters] = useState<ModalFilter | null>(values[6] || null);
  const [brandFilters, setBrandFilters] = useState<ModalFilter | null>(values[7] || null);
  const [shareFilters, setShareFilters] = useState<ModalFilter | null>(values[8]) || null;
  const [campaignTypeFilters, setCampaignTypeFilters] = useState<ModalFilter | null>(values[9]) || null;
  const [adPlatformFilters, setAdPlatformFilters] = useState<ModalFilter | null>(values[10]) || null; 
  const [adPlatformFiltersVisible, setAdPlatformFiltersVisible] = useState<boolean>(false); 
  const [defaultExists, setDefaultExists] = useState<boolean>(false);
  const [sameDefaultFilters, setSameDefaultFilters] = useState<boolean>(false);
  const [viewingDefault, setViewingDefault] = useState<boolean>(false);
  const [focus, setFocus] = useState<boolean>(false);

  const returnAggregatedFilters = (): Array<ModalFilter | null> => {
    const evaluatedShareFilters = retailerFilters ? shareFilters : null;

    return [
      regionFilters,
      retailerFilters,
      marketFilters,
      channelFilters,
      categoryFilters,
      subcategoryFilters,
      segmentFilters,
      brandFilters,
      evaluatedShareFilters,
      campaignTypeFilters,
      adPlatformFilters
    ];
  };

  const returnSetFilterFields = (filters: Array<ModalFilter | null>) => {
    setRegionFilters(filters[0]);
    setRetailerFilters(filters[1]);
    setMarketFilters(filters[2]);
    setChannelFilters(filters[3]);
    setCategoryFilters(filters[4]);
    setSubCategoryFilters(filters[5]);
    setSegmentFilters(filters[6]);
    setBrandFilters(filters[7]);
    setShareFilters(filters[8]);
    setCampaignTypeFilters(filters[9]);
    setAdPlatformFilters(filters[10]);
  };

  const handleFiltersUpdate = async () => {
    // updates all filters based on newly fetched values if they exist
    if (showModal && !isGlobalLoading) {
      setIsGlobalLoading(true);
      try {
        const filters = returnAggregatedFilters();
        const params = returnFormattedSegmentFilters(filters);

        // update region filters if currently applied
        if (regionFilters) {
          const updatedRegionFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/region`,
            params
          );
          setRegionFilters({
            name: copyData?.filter_region_label,
            values: regionFilters?.values.filter((x) => updatedRegionFilters.some((y) => y.id === x.id))
          });
        }
        // update market filters if currently applied
        if (marketFilters) {
          const updatedMarketFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/market`,
            params
          );
          setMarketFilters({
            name: copyData?.filter_market_label,
            values: marketFilters?.values.filter((x) => updatedMarketFilters.some((y) => y.id === x.id))
          });
        }
        // update channel filters if currently applied
        if (channelFilters) {
          const updatedChannelFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/channel`,
            params
          );
          setChannelFilters({
            name: copyData?.filter_channel_label,
            values: channelFilters?.values.filter((x) => updatedChannelFilters.some((y) => y.id === x.id))
          });
        }
        // update retailer filters if currently applied
        if (retailerFilters) {
          const updatedRetailerFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/retailer`,
            params
          );
          setRetailerFilters({
            name: copyData?.filter_retailer_label,
            values: retailerFilters?.values.filter((x) => updatedRetailerFilters.some((y) => y.id === x.id))
          });
        }
        // update adplatform filters if currently applied
        if (adPlatformFilters) {
          const updatedAdPlatformFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/adplatform`,
            params
          );
          setAdPlatformFilters({
            name: copyData?.filter_ad_platform_label,
            values: adPlatformFilters?.values.filter((x) => updatedAdPlatformFilters.some((y) => y.id === x.id))
          });
        }        
        // update category filters if currently applied
        if (categoryFilters) {
          console.log('categoryFilters');
          const updatedCategoryFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/category`,
            params
          );
          setCategoryFilters({
            name: copyData?.filter_category_label,
            values: categoryFilters?.values.filter((x) => updatedCategoryFilters.some((y) => y.id === x.id))
          });
        }
        // update subcategory filters if currently applied
        if (subcategoryFilters) {
          console.log('subcategoryFilters');
          const updatedSubCategoryFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/subcategory`,
            params
          );
          setSubCategoryFilters({
            name: copyData?.filter_subcategory_label,
            values: subcategoryFilters?.values.filter((x) => updatedSubCategoryFilters.some((y) => y.id === x.id))
          });
        }
        // update segment filters if currently applied
        if (segmentFilters) {
          const updatedSegmentFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/segment`,
            params
          );
          setSegmentFilters({
            name: copyData?.filter_segment_label,
            values: segmentFilters?.values.filter((x) => updatedSegmentFilters.some((y) => y.id === x.id))
          });
        }
        // update brand filters if currently applied
        if (brandFilters) {
          const updatedBrandFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/brand`,
            params
          );
          setBrandFilters({
            name: copyData?.filter_brand_label,
            values: brandFilters?.values.filter((x) => updatedBrandFilters.some((y) => y.id === x.id))
          });
        }
        // clear global loading state to allow for further UI interactions
        setIsGlobalLoading(false);
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handleAdPlatformVisibility = async () => {
    // Hide ad platform if there is no values on it
    if (showModal) {
      try {
        const filters = returnAggregatedFilters();
        const params = returnFormattedSegmentFilters(filters);

        // fetch ad platform values with current parameters
          const updatedAdPlatformFilters: Array<FO> = await fetchCall(
            `${config.react_app_api_base_url}/filter/adplatform`,
            params
          );

          setAdPlatformFiltersVisible(updatedAdPlatformFilters && updatedAdPlatformFilters.length > 0);
      
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handleSaveDefaultFilters = async () => {
    const updatedFilters = returnAggregatedFilters();
    dispatch(SavedActions.setSavedMarketFilters(updatedFilters));
  };

  const handleViewDefault = () => {
    setViewingDefault(true);
    returnSetFilterFields(savedMarket);
  };

  const handleFiltersSubmission = async () => {
    const updatedFilters = returnAggregatedFilters();
    if (!retailerFilters) {
      setFocus(true);
    } else {
      onUpdate(updatedFilters);
      setShowModal(false);
    }
  };

  const handleFilterClear = () => {
    setViewingDefault(true);
    //TODO return to all null array after MVP launch
    // const arrOfNulls = new Array(savedMarket.length).fill(null);
    let arrOfNulls = new Array(savedMarket.length);
    const staticArray: Array<string> = ['Retailer', 'Share'];
    arrOfNulls = savedMarket.map((filterItem) => {
      const fName: string = filterItem?.name || '';
      const shareDefault = savedMarket[8];
      return staticArray.includes(fName) ? (fName === 'Share' ? shareDefault : filterItem) : null;
    });
    returnSetFilterFields(arrOfNulls);
  };

  useEffect(() => {
    /*
      Ad platform filter for now is the only filter that is hidden, if there was no values on it.
      This could be generating more requests than necessary. 
    */
    handleAdPlatformVisibility();
  }, [  regionFilters,
        marketFilters,
        channelFilters,
        retailerFilters,
        categoryFilters,
        subcategoryFilters,
        segmentFilters,
        brandFilters,
        showModal]);


  useEffect(() => {
    /*
      update individual filter values based on any updated value props
      and render state (to reset values if changed but not saved)
    */
    if (values !== savedMarket) {
      setViewingDefault(false);
    }
    returnSetFilterFields(values);
  }, [values, showModal]);

  useEffect(() => {
    // if updated retailer filters don't include any amazon type(s)
    if (retailerFilters) {
      setFocus(false);
    }
    if (!(retailerFilters?.values && retailerFilters.values?.length == 1 && retailerFilters.values[0].name.toLowerCase().includes('amazon'))) {
      setShareFilters(null);
      setCampaignTypeFilters(null);
    } else if (!viewingDefault) {
      if (values[8] === null) {
        setShareFilters({
          name: 'Share',
          values: [
            {
              id: '1P',
              name: '1P'
            }
          ]
        });
      } else {
        setShareFilters(values[8]);
      }
      setCampaignTypeFilters(values[9]);
    }
    handleFiltersUpdate();
  }, [
    regionFilters,
    marketFilters,
    channelFilters,
    retailerFilters,
    categoryFilters,
    subcategoryFilters,
    segmentFilters,
    brandFilters,
    adPlatformFilters
  ]);

  useEffect(() => {
    const updatedFilters = returnAggregatedFilters();
    const savedEqualInitial = isEqual(savedMarket, defaultMarketFilters);
    const savedMarketFiltersSet = !isEqual(savedMarket, defaultMarketFilters);
    const sameFilters = isEqual(savedMarket, updatedFilters);
    const defaultFiltersChosen = isEqual(updatedFilters, defaultMarketFilters);

    setDefaultExists(savedEqualInitial || defaultFiltersChosen || savedMarketFiltersSet);
    setSameDefaultFilters(sameFilters);
  }, [returnAggregatedFilters]);

  const optionsColStyles = 'w-full mr-[28px] last:mr-0';

  return (
    <div>
      <div
        id="filter_modal_button"
        className="relative cursor-pointer py-0 px-2 h-10 box-border rounded border border-borderGray shadow-filterModalShadow flex items-center bg-white font-nunitoLight text-sm text-fontBlock"
        onClick={() => setShowModal(!showModal)}
      >
        <FilterIcon className="fill-current text-fontBlock" />
        <span className="ml-[7px] leading-[21px]">{copyData?.filter_button}</span>
      </div>
      {showModal && (
        <div className="font-nunitoRegular flex justify-center items-center fixed top-0 left-0 h-screen w-screen bg-green-200 bg-[rgba(27,27,27,0.5)] z-[1000] px-6">
          <div className="relative bg-white w-full max-w-screen-lg rounded py-9 px-10">
            {isGlobalLoading && (
              <div className="absolute top-0 left-0 h-full w-full bg-[rgba(256,256,256,0.6)] z-[100] flex items-center justify-center">
                <PulseLoader color={colors.navy} size={15} />
              </div>
            )}
            <ExitIcon
              className="cursor-pointer absolute top-[31px] right-[25px]"
              onClick={() => setShowModal(!showModal)}
            />
            <p className="m-0 text-[22px] leading-6 tracking-[0.15px] font-nunitoBold text-[#2b2b2b]">
              {copyData?.filter_by_label}:
            </p>

            <div className="mt-6 flex">
              <div className={optionsColStyles}>
                <FilterOption
                  filters={returnAggregatedFilters()}
                  label={copyData?.filter_region_label}
                  values={regionFilters ? regionFilters.values : []}
                  onChange={(filter) => setRegionFilters(filter)}
                />
                <FilterOption
                  filters={returnAggregatedFilters()}
                  label={copyData?.filter_market_label}
                  values={marketFilters ? marketFilters.values : []}
                  onChange={(filter) => setMarketFilters(filter)}
                />
                <FilterOption
                  filters={returnAggregatedFilters()}
                  label={copyData?.filter_category_label}
                  values={categoryFilters ? categoryFilters.values : []}
                  onChange={(filter) => setCategoryFilters(filter)}
                />
                {retailerFilters?.values.some((value) => value.name.toLowerCase().includes('amazon')) &&
                  retailerFilters?.values?.length === 1 && (
                    <FilterOption
                      filters={returnAggregatedFilters()}
                      label={copyData?.filter_campaign_type_label}
                      subLabel={copyData?.filter_campaign_type_sub_label}
                      values={campaignTypeFilters ? campaignTypeFilters.values : []}
                      onChange={(filter) => setCampaignTypeFilters(filter)}
                    />
                  )}
              </div>

              <div className={optionsColStyles}>
                <FilterOption
                  filters={returnAggregatedFilters()}
                  label={copyData?.filter_retailer_label}
                  values={retailerFilters ? retailerFilters.values : []}
                  onChange={(filter) => setRetailerFilters(filter)}
                  required={true}
                  focus={focus}
                />
                {adPlatformFiltersVisible && <FilterOption
                  filters={returnAggregatedFilters()}
                  label={copyData?.filter_ad_platform_label}
                  values={adPlatformFilters ? adPlatformFilters.values : []}
                  onChange={(filter) => setAdPlatformFilters(filter)}
                />}
                <FilterOption
                  filters={returnAggregatedFilters()}
                  label={copyData?.filter_brand_label}
                  values={brandFilters ? brandFilters.values : []}
                  onChange={(filter) => setBrandFilters(filter)}
                />
                {retailerFilters?.values.some((value) => value.name.toLowerCase().includes('amazon')) &&
                  retailerFilters?.values?.length === 1 && (
                    <FilterOption
                      filters={returnAggregatedFilters()}
                      label={copyData?.filter_subcategory_label}
                      values={subcategoryFilters ? subcategoryFilters.values : []}
                      onChange={(filter) => setSubCategoryFilters(filter)}
                    />
                  )}
                {/*
                  Only render if retailer filters include an amazon type
                */}
                {retailerFilters?.values.some((value) => value.name.toLowerCase().includes('amazon')) &&
                  retailerFilters?.values?.length === 1 && (
                    <FilterOption
                      viewingDefault={viewingDefault}
                      filters={returnAggregatedFilters()}
                      label={copyData?.filter_share_label}
                      values={shareFilters ? shareFilters.values : []}
                      onChange={(filter) => setShareFilters(filter)}
                    />
                  )}
              </div>
            </div>

            <div className="flex justify-between border-t border-[#d8d8d8] pt-6 mt-2">
              <div>
                <BaseButton
                  onClick={handleSaveDefaultFilters}
                  variant={!defaultExists || !sameDefaultFilters ? 'tertiary' : 'disabled'}
                  automationID="filter-modal_button_save-default"
                >
                  {copyData?.save_default_button}
                </BaseButton>
                <BaseButton
                  onClick={handleViewDefault}
                  variant={defaultExists && !sameDefaultFilters ? 'secondary' : 'disabled'}
                  automationID="filter-modal_button_view-default"
                >
                  {copyData?.view_default_button}
                </BaseButton>
              </div>
              <div>
                <BaseButton onClick={handleFilterClear} variant="secondary" automationID="filter-modal_button_clear">
                  {copyData?.clear_button}
                </BaseButton>
                <BaseButton onClick={handleFiltersSubmission} automationID="filter-modal_button_apply">
                  {copyData?.apply_button}
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterModal;
